'use client';

import { useCallback } from 'react';
import { ReactComponent as Tag } from '@assets/icons/tag.svg';
import { useShowSvcPrice } from 'context/ShowSvcPriceContext';
import parse from 'html-react-parser';
import Image from 'next/image';

import { Box, Flex, Stack, Text, useTheme } from '@chakra-ui/react';

import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setSelectedRecommendedProducts } from 'store/reducers/bookingAppointmentSlice';

import { formatCurrency } from 'utils';

import { ProductItem as ProductItemType, SelectedProductType } from 'types';

import { Counter, Divider } from 'ui';

interface RecommendedItemProps {
  item: ProductItemType;
  isLastItem?: boolean;
}

const RecommendedItem = ({
  item,

  isLastItem = false
}: RecommendedItemProps) => {
  const {
    colors: {
      palette: {
        booking: {
          stepRecommended: {
            list: { item: listItem }
          }
        },
        shop: {
          packages: {
            stepPackages: { subPackages: subPackagesColors }
          }
        }
      }
    }
  } = useTheme();

  const dispatch = useAppDispatch();
  const showSvcPrice = useShowSvcPrice();
  const { selectedProducts = [] } = useAppSelector(
    (state) => state.bookingAppointment
  );

  const isDiscountExist = item.base_price > 0 && item.base_price > item.price;

  const handleChangeAmount = useCallback(
    (value: number) => {
      let updatedelectedProducts: SelectedProductType[] = [];

      if (value === 0) {
        updatedelectedProducts = selectedProducts.filter(
          (product) => product.id !== item.id
        );
      } else {
        const isExist = selectedProducts.some(
          (product) => product.id === item.id
        );

        if (isExist) {
          updatedelectedProducts = selectedProducts.map((product) => {
            if (product.id === item.id) {
              return { ...product, qty: value };
            }

            return product;
          });
        } else {
          updatedelectedProducts = [
            ...selectedProducts,
            { id: item.id, qty: 1, price: item.price }
          ];
        }
      }

      dispatch(setSelectedRecommendedProducts(updatedelectedProducts));
    },
    [dispatch, item.id, item.price, selectedProducts]
  );

  const renderCounter = () => {
    const getCounterValue = () => {
      let counterValue = 0;
      if (selectedProducts.length > 0) {
        const selectedPackage = selectedProducts.find(
          ({ id }) => id === item.id
        );
        counterValue = selectedPackage?.qty || 0;
      }
      return counterValue;
    };

    return (
      <Counter
        minValue={0}
        maxValue={99}
        value={getCounterValue()}
        onChange={handleChangeAmount}
      />
    );
  };

  return (
    <Stack w='100%' gap={0}>
      <Flex
        flexDirection={{
          base: 'column',
          md: 'row'
        }}
        px={{ base: 15, md: '30px' }}
        py='15px'
        alignItems='center'
      >
        <Box display={{ base: 'none', sm: 'block' }} flexShrink={0}>
          <Flex
            alignItems='center'
            justifyContent={item?.primary_img_x2_url ? 'start' : 'center'}
            my='auto'
            position='relative'
            w='80px'
            h='80px'
            bg={
              item?.primary_img_x2_url
                ? listItem.imageBg
                : listItem.emptyImageBg
            }
            borderRadius='5px'
            mr='25px'
          >
            {item?.primary_img_x2_url ? (
              <Image
                src={item.primary_img_x2_url}
                alt='product'
                style={{ objectFit: 'contain', borderRadius: '5px' }}
                fill
                sizes='(max-width: 450px) 80px, 140px'
              />
            ) : (
              <Tag fill={listItem.defaultIconColor} width={32} height={32} />
            )}
          </Flex>
        </Box>
        <Flex
          flexDirection='column'
          justifyContent='center'
          w='full'
          maxH={item?.dscr ? '120px' : 'auto'}
          h={item?.dscr ? '120px' : 'auto'}
        >
          <Box>
            <Text
              fontSize={{ base: '19px', lg: '21px' }}
              lineHeight={{ base: '17px', lg: '28px' }}
              fontWeight={500}
              color={listItem.titleColor}
              mb='3px'
            >
              {item.name}
            </Text>
            {item?.dscr_line_1 && (
              <Text
                fontSize={{ base: '14px', lg: '16px' }}
                lineHeight={{ base: '20px', lg: '22px' }}
                color={listItem.subTitleColor}
              >
                {item?.dscr_line_1}
              </Text>
            )}
          </Box>
          {item?.dscr && (
            <Text
              fontSize='14px'
              lineHeight={1.6}
              color={listItem.descriptionColor}
              mt='15px'
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical'
              }}
            >
              {parse(item?.dscr ?? '')}
            </Text>
          )}
        </Flex>

        <Flex
          w={{
            base: 'full',
            md: 'auto'
          }}
          mt={{
            base: 2,
            md: 0
          }}
          justifyContent={{
            base: 'space-between',
            md: 'flex-end'
          }}
        >
          <Flex
            flexDirection='column'
            minW={{
              base: 'auto',
              md: '240px'
            }}
            justifyContent='center'
            alignItems={{
              base: 'flex-start',
              md: 'flex-end'
            }}
          >
            {showSvcPrice && (
              <>
                <Text
                  fontSize={{ base: '22px', lg: '24px' }}
                  fontWeight='semibold'
                  color={listItem.priceColor}
                >
                  {formatCurrency(item.price)}
                </Text>
                {isDiscountExist && (
                  <Text
                    fontSize={{ base: '12px', lg: '14px' }}
                    color={listItem.discountPriceColor}
                  >
                    {`Original price ${formatCurrency(item.base_price)}`}
                  </Text>
                )}
              </>
            )}
          </Flex>
          <Flex
            ml={{
              base: 0,
              md: '60px'
            }}
            flexDirection='column'
          >
            {renderCounter()}
          </Flex>
        </Flex>
      </Flex>

      {!isLastItem && (
        <Divider borderColor={subPackagesColors.dividerInside} opacity={0.1} />
      )}
    </Stack>
  );
};

export default RecommendedItem;
